import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Home from "@/pages/Home";
import ServiceLanding from "@/pages/ServiceLanding";
import { serviceLandingData } from "@/data/serviceLandingData";
import ThankYou from "@/pages/ThankYou";
import Impressum from "@/pages/Impressum";
import Privacy from "@/pages/Privacy";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminContent from "@/pages/admin/AdminContent";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminAILeads from "@/pages/admin/AdminAILeads";
import AdminGallery from "@/pages/admin/AdminGallery";
import AdminTracking from "@/pages/admin/AdminTracking";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminSecurity from "@/pages/admin/AdminSecurity";
import WhatsAppRedirect from "@/pages/redirects/WhatsAppRedirect";
import CallRedirect from "@/pages/redirects/CallRedirect";
import FormRedirect from "@/pages/redirects/FormRedirect";
import CookieConsent from "@/components/CookieConsent";
import ChatWidget from "@/components/ChatWidget";
import StickyButtons from "@/components/StickyButtons";
import { useEffect, useState, useCallback } from "react";
import api from "@/utils/api";
import { initTracking, getConsent, initConsentDefaults, updateConsent } from "@/utils/tracking";
import { initUtmSession, trackEvent } from "@/utils/trackingClient";
import { initVisitorSession, trackPageView, installUniversalTracking, trackEvent as attrTrack } from "@/utils/attribution";

function AppContent() {
  const [settings, setSettings] = useState(null);
  const [content, setContent] = useState(null);
  const [showCookieConsent, setShowCookieConsent] = useState(false);
  const location = useLocation();

  // Check if current route is an admin route
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Initialize Consent Mode v2 defaults BEFORE anything else
  useEffect(() => {
    initConsentDefaults();
    initUtmSession();
    initVisitorSession();
    installUniversalTracking();
  }, []);

  // Auto Page-View Tracking bei Route-Wechsel (für Funnel + Visitor-Aggregation)
  useEffect(() => {
    trackPageView();
  }, [location.pathname, location.search]);

  useEffect(() => {
    // Load site settings and content
    api.get('/settings')
      .then(res => setSettings(res.data))
      .catch(err => console.error('Failed to load settings:', err));
    api.get('/content')
      .then(res => { if (res.data && res.data.hero) setContent(res.data); })
      .catch(err => console.error('Failed to load content:', err));
  }, []);

  useEffect(() => {
    // Check consent status on mount
    const consent = getConsent();
    if (!consent) {
      setShowCookieConsent(true);
    } else if (consent.marketing && settings) {
      // Only init tracking if user previously consented
      updateConsent(true);
      initTracking(settings.gtm_id, settings.meta_pixel_id, settings.ga4_id);
    }
  }, [settings]);

  const handleCookieConsent = useCallback((consent) => {
    setShowCookieConsent(false);
    if (consent.marketing && settings) {
      updateConsent(true);
      initTracking(settings.gtm_id, settings.meta_pixel_id, settings.ga4_id);
      attrTrack("cookie_accept", { marketing: true, analytics: !!consent.analytics });
    } else {
      updateConsent(false);
      attrTrack("cookie_decline", { marketing: false });
    }
  }, [settings]);

  // CRP-011: Cookie-basierte Auth — Session wird serverseitig via /auth/me geprüft
  const ProtectedRoute = ({ children }) => {
    const [authState, setAuthState] = useState(null);
    useEffect(() => {
      api.get('/auth/me')
        .then(() => setAuthState(true))
        .catch(() => setAuthState(false));
    }, []);
    if (authState === null) return null;
    return authState ? children : <Navigate to="/admin/login" />;
  };

  return (
    <>
      <Routes>
        <Route path="/" element={<Home settings={settings} content={content} />} />
        {/* Gewerk-Landingpages (SEO/Google Ads) */}
        {serviceLandingData.map((svc) => (
          <Route
            key={svc.slug}
            path={`/${svc.slug}`}
            element={<ServiceLanding slug={svc.slug} settings={settings} content={content} />}
          />
        ))}
        <Route path="/anfrage-erfolgreich" element={<ThankYou settings={settings} />} />
        <Route path="/danke" element={<ThankYou settings={settings} />} />
        <Route path="/impressum" element={<Impressum />} />
        <Route path="/datenschutz" element={<Privacy />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route 
          path="/admin/dashboard" 
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/content" 
          element={
            <ProtectedRoute>
              <AdminContent />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/settings" 
          element={
            <ProtectedRoute>
              <AdminSettings />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/ai-leads" 
          element={
            <ProtectedRoute>
              <AdminAILeads />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/gallery" 
          element={
            <ProtectedRoute>
              <AdminGallery />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/tracking" 
          element={
            <ProtectedRoute>
              <AdminTracking />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/analytics" 
          element={
            <ProtectedRoute>
              <AdminAnalytics />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/security" 
          element={
            <ProtectedRoute>
              <AdminSecurity />
            </ProtectedRoute>
          } 
        />
        {/* Conversion-Tracking Redirect Routes */}
        <Route path="/whatsapp" element={<WhatsAppRedirect />} />
        <Route path="/anruf" element={<CallRedirect />} />
        <Route path="/formular" element={<FormRedirect />} />
      </Routes>
      {showCookieConsent && !isAdminRoute && (
        <CookieConsent onConsent={handleCookieConsent} />
      )}
      {!isAdminRoute && !['/anfrage-erfolgreich', '/danke', '/whatsapp', '/anruf', '/formular'].includes(location.pathname) && (
        <>
          <StickyButtons settings={settings} />
          <ChatWidget settings={settings} />
        </>
      )}
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </div>
  );
}

export default App;